import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Heart, Users, Award, Phone, MapPin, Zap } from "lucide-react";
import SEO from "../components/SEO";
import FleetSection from "../components/FleetSection";
import { PHONE, PHONE_TEL, trustSignals } from "../data/content";

export default function About() {
  return (
    <>
      <SEO
        title="About Tech Emergency | Adelaide's Local Mobile IT Team"
        description="Tech Emergency is an Adelaide-based, owner-operated mobile computer repair and home IT service. Police-checked, family-safe, fair pricing — no big-corporate call centres."
        path="/about"
      />
      <section className="pt-20 pb-16 te-noise">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-4">/ About us</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-none mb-6">
            Your local<br />tech problem solvers.
          </h1>
          <p className="text-zinc-400 max-w-2xl text-lg leading-relaxed">
            We're not just another IT support company. We're your neighbours — a local Adelaide
            technician who shows up on time, explains things in plain English, and actually fixes
            problems instead of patching them.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-6">
          {[
            { Icon: MapPin, title: "Adelaide. Only.", body: "No national franchise, no overseas call centre. Every technician lives and works in South Australia." },
            { Icon: Zap, title: "Zero callout fee", body: "We don't charge to come to you. Our fleet is electric — running costs are tiny, so we pass the saving back to you." },
            { Icon: ShieldCheck, title: "Police-checked", body: "Your technician carries a current SA Police clearance. Owner-operated, family-safe and senior-friendly service." },
            { Icon: Heart, title: "Fair, transparent pricing", body: "Upfront quotes before any work begins. No surprise fees, no upsell." },
            { Icon: Users, title: "Homes across Adelaide", body: "From grandma's iPad to the family's whole-home Wi-Fi — we cover the lot." },
            { Icon: Award, title: "Google 4.9 stars", body: "Adelaide locals rate our service highly — read the reviews before you book." },
            { Icon: Phone, title: "Real humans answer", body: "When you call 1800 836 390, you talk to someone who can actually help — not a phone tree." },
          ].map((v) => (
            <div key={v.title} className="te-card p-8">
              <v.Icon size={22} style={{ color: "var(--te-primary)" }} className="mb-4" />
              <h3 className="text-xl font-semibold tracking-tight mb-2">{v.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ZERO CALLOUT FEE + ELECTRIC FLEET — the full story */}
      <FleetSection />

      {/* CHARGING-BAY PROOF SHOT — visual proof of the electric claim */}
      <section className="pb-20 md:pb-28" data-testid="about-charging-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="te-overline mb-4">/ Between jobs</div>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter leading-tight mb-6">
                Charging up<br />
                <span style={{ color: "var(--te-primary)" }}>between Adelaide jobs.</span>
              </h2>
              <p className="text-lg text-zinc-300 leading-relaxed mb-5">
                Instead of $80 of petrol per day, our running cost is closer to <strong className="text-white">$8 of electricity</strong>.
              </p>
              <p className="text-zinc-400 leading-relaxed mb-5">
                That difference is exactly what would have been your callout fee. Every kilometre we drive
                is powered by South Australian grid energy — much of which now comes from renewables — so
                getting a technician to your door is genuinely greener too.
              </p>
              <p className="text-zinc-500 text-sm leading-relaxed italic">
                Spotted between jobs at a public charging bay — our fully-electric mobile IT fleet.
              </p>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <div
                className="relative aspect-[4/3] border border-white/10 overflow-hidden"
                style={{ background: "var(--te-paper)" }}
              >
                <img
                  src="/images/fleet/geely-ex5-charging.jpg"
                  alt="Tech Emergency Geely EX5 charging up between Adelaide IT jobs"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover z-[1]"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div
                  className="absolute top-4 left-4 px-3 py-1.5 flex items-center gap-1.5 z-10"
                  style={{ background: "rgba(5,5,5,0.85)", backdropFilter: "blur(6px)" }}
                >
                  <Zap size={12} fill="var(--te-primary)" stroke="var(--te-primary)" />
                  <span className="te-overline text-[10px] text-white">EV CHARGING · ADELAIDE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="p-8 md:p-12 border border-white/10 grid md:grid-cols-2 gap-10 items-center"
            style={{ backgroundColor: "var(--te-paper)" }}
          >
            <div>
              <div className="te-overline mb-3">/ Our promise</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-4">
                If we can't fix it, you don't pay for the visit.
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Simple. We're confident in what we do — so confident that we put the risk on us, not you.
              </p>
              <a href={PHONE_TEL} className="te-btn te-btn-primary"><Phone size={16}/> {PHONE}</a>
            </div>
            <ul className="space-y-3">
              {trustSignals.map((t) => (
                <li key={t} className="flex items-start gap-3 text-zinc-300">
                  <ShieldCheck size={18} style={{ color: "var(--te-primary)" }} className="mt-0.5 flex-shrink-0"/>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
